import React, { createContext, useContext, useState, useEffect } from 'react'
import apiService from '../services/api'

const AuthContext = createContext()

export function useAuth() {
  const context = useContext(AuthContext)
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}

export function AuthProvider({ children }) {
  const [isAuthenticated, setIsAuthenticated] = useState(false)
  const [user, setUser] = useState(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    // Check if user is already logged in
    const token = localStorage.getItem('admin_token')
    
    if (token) {
      // Verify token with backend
      apiService.getCurrentUser()
        .then(response => {
          if (response.success) {
            setIsAuthenticated(true)
            setUser(response.data) // User data is directly in response.data
            localStorage.setItem('admin_user', JSON.stringify(response.data))
          } else {
            // Invalid token, clear storage
            localStorage.removeItem('admin_token')
            localStorage.removeItem('admin_user')
          }
        })
        .catch(error => {
          console.error('Token verification failed:', error)
          // Invalid token, clear storage
          localStorage.removeItem('admin_token')
          localStorage.removeItem('admin_user')
        })
        .finally(() => {
          setLoading(false)
        })
    } else {
      setLoading(false)
    }
  }, []) // Empty dependency array to run only once

  const login = async (email, password) => {
    try {
      const response = await apiService.login(email, password)
      
      if (response.success) {
        const { user, tokens } = response.data
        
        // Extract accessToken from tokens object
        const token = tokens?.accessToken
        
        if (!token) {
          return { success: false, error: 'No authentication token received' }
        }
        
        localStorage.setItem('admin_token', token)
        localStorage.setItem('admin_user', JSON.stringify(user))
        
        setIsAuthenticated(true)
        setUser(user)
        return { success: true }
      } else {
        return { success: false, error: response.message || 'Login failed' }
      }
    } catch (error) {
      console.error('Login error:', error)
      return { success: false, error: error.message || 'Login failed' }
    }
  }

  const logout = async () => {
    try {
      await apiService.logout()
    } catch (error) {
      console.error('Logout error:', error)
    } finally {
      localStorage.removeItem('admin_token')
      localStorage.removeItem('admin_user')
      setIsAuthenticated(false)
      setUser(null)
    }
  }

  const forgotPassword = async (email) => {
    try {
      const response = await apiService.forgotPassword(email)
      return { success: response.success, message: response.message, resetToken: response.resetToken }
    } catch (error) {
      console.error('Forgot password error:', error)
      return { success: false, error: error.message || 'Failed to send reset email' }
    }
  }

  const resetPassword = async (token, newPassword) => {
    try {
      const response = await apiService.resetPassword(token, newPassword)
      return { success: response.success, message: response.message }
    } catch (error) {
      console.error('Reset password error:', error)
      return { success: false, error: error.message || 'Failed to reset password' }
    }
  }

  const value = {
    isAuthenticated,
    user,
    login,
    logout,
    forgotPassword,
    resetPassword,
    loading
  }

  return (
    <AuthContext.Provider value={value}>
      {!loading && children}
    </AuthContext.Provider>
  )
}
